import React, { Fragment, useCallback, useEffect, useMemo, useRef, KeyboardEvent } from 'react';
import { Section } from '@repo/components/containers';
import Container from '@components/ContentContainer';
import { List as VirtualizedList, createListContext } from '@components/List/VirtualizedList';
import Header from '@components/Header';
import styles from './Duplicates.module.scss';
import { PeopleHeader } from '@views/People';
import { ListRowContent } from '@repo/components/lists';
import { ActionIcon } from '@views/Activity/ActivityListItem/ItemActions/ItemActions';
import { ActionArchiveIcon, ActionIgnoreIcon, ActionMergeIcon, LoaderIcon } from '@repo/icons';
import { AngledContactAvatarGroup } from '@components/ContactAvatar';
import ContactLink from '@components/ContactLink';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedDuplicate } from '@repo/redux/reducers/ui/duplicatesView';
import {
  fetchDuplicates,
  ignoreDuplicate,
  fetchMoreDuplicates,
  setSelectedDuplicate,
} from '@repo/redux/actions/duplicates';
import type Duplicate from '@repo/utils/models/Duplicate';
import ClearedDuplicates from './ClearedDuplicates';
import { ViewID } from '@repo/utils/types';
import DuplicateSidebar from './DuplicateSidebar';
import { selectDuplicatesCollection, selectDuplicate } from '@repo/redux/reducers/data/duplicates';
import cn from 'classnames';
import {
  archiveDuplicateMemberProfiles,
  mergeDuplicateMemberProfiles,
} from '@views/CommandBar/actionsBySection/duplicateActions';
import { useScrollRestoration } from '@components/utils/use-scroll-restoration';

const DuplicateRowActions = ({ duplicate }) => {
  const dispatch = useDispatch();

  return (
    <ListRowContent className={styles.rowActions}>
      <ActionIcon
        className={cn(styles.actionIcon, styles.archiveAction)}
        handler={() => archiveDuplicateMemberProfiles(duplicate, dispatch)}
        icon={<ActionArchiveIcon width={22} height={22} hoverOpacity />}
        shortcut="A"
        label="Archive Profiles"
      />
      <ActionIcon
        className={styles.actionIcon}
        handler={() => dispatch(ignoreDuplicate(duplicate.id))}
        icon={<ActionIgnoreIcon width={19} height={19} hoverOpacity />}
        shortcut="I"
        label="Ignore Profiles"
      />
      <ActionIcon
        className={styles.actionIcon}
        handler={() => mergeDuplicateMemberProfiles(duplicate, dispatch)}
        icon={<ActionMergeIcon width={21.5} height={21.5} hoverOpacity />}
        shortcut="M"
        label="Merge Profiles"
      />
    </ListRowContent>
  );
};

function DuplicateRow({ duplicateID, isHighlighted }) {
  const duplicate = useSelector(selectDuplicate(duplicateID));
  const contacts = duplicate.contacts;

  return (
    <>
      <ListRowContent
        className={cn(
          styles.rowContent,
          duplicate.isMerging && styles.merging,
          duplicate.isTransitioningOut && styles.transitioningOut,
        )}
      >
        <AngledContactAvatarGroup contacts={contacts} className={styles.contactAvatars} />
        <div className={styles.contactNames}>
          {contacts.map((contact, idx) => (
            <Fragment key={contact.id}>
              <ContactLink key={contact.id} contact={contact} className={styles.contactLink}>
                {contact.displayName}
              </ContactLink>
              {idx < contacts.length - 1 && <>, </>}
            </Fragment>
          ))}
        </div>
      </ListRowContent>
      {isHighlighted && !duplicate.isMerging && <DuplicateRowActions duplicate={duplicate} />}
      {duplicate.isMerging && <LoaderIcon />}
    </>
  );
}

const renderDuplicateRow = (item, isHighlighted) => {
  return <DuplicateRow duplicateID={item.id} isHighlighted={isHighlighted} />;
};

const DuplicatesListContext = createListContext();
const viewID: ViewID = 'Duplicates';

export default function Duplicates() {
  const dispatch = useDispatch();
  const scrollingContainerRef = useRef(null);
  const selectedDuplicate = useSelector(selectSelectedDuplicate);
  const duplicatesCollection = useSelector(selectDuplicatesCollection);
  const duplicates = useMemo(() => {
    if (!duplicatesCollection.updated || duplicatesCollection.items.size === 0) {
      return [];
    }

    return duplicatesCollection.items.valueSeq().toArray();
  }, [duplicatesCollection.updated, duplicatesCollection.items]);

  const defaultHighlightedIdx = useMemo(() => {
    if (selectedDuplicate) {
      const highlightedIdx = duplicates.findIndex(({ id }) => id == selectedDuplicate.id);
      return highlightedIdx || 0;
    }
    return 1;
  }, []);

  const onHotKey = useCallback((duplicate: Duplicate, e: KeyboardEvent) => {
    if (!duplicate || duplicate?.isMerging || duplicate?.isTransitioningOut) {
      return;
    }

    switch (e.key?.toLowerCase()) {
      case 'a':
        archiveDuplicateMemberProfiles(duplicate, dispatch);
        return;
      case 'i':
        dispatch(ignoreDuplicate(duplicate.id));
        return;
      case 'm':
        mergeDuplicateMemberProfiles(duplicate, dispatch);
        return;
    }
  }, []);

  useScrollRestoration({ viewID, scrollingContainerRef });

  useEffect(() => {
    dispatch(fetchDuplicates());
  }, [dispatch]);

  const loadNextPage = useCallback(() => dispatch(fetchMoreDuplicates()), [dispatch]);

  const renderDuplicates = () => {
    if (duplicatesCollection.isLoaded && duplicatesCollection.items.size === 0) {
      return <ClearedDuplicates />;
    }

    return (
      <VirtualizedList
        shouldResetHighlight={false}
        items={duplicates}
        renderItem={renderDuplicateRow}
        canLoadMore={duplicatesCollection.canLoadMore}
        listRowClassName={styles.listRow}
        loadMore={loadNextPage}
        ListContext={DuplicatesListContext}
        estimatedCount={duplicatesCollection.persistedItemsOnServer}
        onItemHighlight={(duplicate: Duplicate | undefined) => {
          dispatch(setSelectedDuplicate(duplicate?.id));
        }}
        headerCutoff={1}
        ref={scrollingContainerRef}
        defaultHighlightedIdx={defaultHighlightedIdx}
        determineItemSize={(_) => 56}
        onHotKey={onHotKey}
        contextMenuTarget="Duplicate"
        hasListHeader
      />
    );
  };

  return (
    <Container>
      <Container.Primary
        className={styles.container}
        id="content"
        header={
          <Header className={styles.listHeader} actions={false}>
            <div className={styles.listHeaderInner}>
              <PeopleHeader />
            </div>
          </Header>
        }
      >
        <Section>{renderDuplicates()}</Section>
      </Container.Primary>
      <Container.Sidebar>
        {selectedDuplicate && (
          <DuplicateSidebar duplicateID={selectedDuplicate.id} key={selectedDuplicate.id} />
        )}
      </Container.Sidebar>
    </Container>
  );
}
